import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useOfflineCompat } from "@/composables/useOfflineCompat";
import { useRest } from "@/composables/useRest";

const BASE_URL = `${process.env.VUE_APP_BACKEND_ENVIRONMENT}/parametros/dependencia`;

export const useDependenciasStore = defineStore("dependencias", () => {
  // --- Catálogo legacy (usado por otros módulos) ---
  const dependencias = ref([]);
  const dependenciasListLoad = ref(false);

  const dependenciasList = ref([]);
  const allDependencias = ref([]);
  const allDependenciasListLoad = ref(false);
  const catalogNeedsRefresh = ref(false);
  const isLoading = ref(false);
  const pageSize = ref(6);
  const currentPage = ref(1);
  const totalDependencias = ref(0);

  const offline = useOfflineCompat();
  const rest = useRest();

  const offset = computed(() => (currentPage.value - 1) * pageSize.value);
  const totalPages = computed(() => Math.ceil(totalDependencias.value / pageSize.value));

  async function setDependencias(refresh = false) {
    try {
      let datos = [];
      if (dependenciasListLoad.value && !refresh) return;

      if (!refresh && offline.offlineServer) {
        let r = await offline.getList("Dependencias");
        if (Array.isArray(r.data.data) && r.data.data.length > 0 && r.data.data[0].list) {
          datos = r.data.data[0].list;
        }
      }

      if (!datos || refresh || datos.length < 1) {
        const r = await rest.get({ url: BASE_URL });
        datos = r.data.data.map((element) => ({
          dependencia: element.Nombre,
          tel: element.Telefonos,
          localidad: element.Departamento,
          id: element.DependenciaID,
          coordenadas1: null,
          coordenadas2: null,
        }));
        offline.setList("Dependencias", datos);
      }

      dependencias.value = datos;
      dependenciasListLoad.value = true;
    } catch (e) {
      console.error("setDependencias>>", e);
    }
  }

  async function getTotal(searchQuery = null) {
    try {
      let url = `${BASE_URL}?getTotal=true`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      const { data } = await rest.get({ url });
      totalDependencias.value = +data.doc.total;
    } catch (e) {
      console.error("getTotal>>", e);
    }
  }

  async function setAllDependencias(refresh = false) {
    try {
      if (allDependenciasListLoad.value && !refresh && !catalogNeedsRefresh.value) {
        return allDependencias.value;
      }
      const r = await rest.get({ url: BASE_URL });
      allDependencias.value = r.data.data;
      allDependenciasListLoad.value = true;
      catalogNeedsRefresh.value = false;
      return allDependencias.value;
    } catch (e) {
      console.error("setAllDependencias>>", e);
      return allDependencias.value;
    }
  }

  async function getDependencia(id) {
    try {
      const r = await rest.get({ url: `${BASE_URL}/${id}` });
      const data = r.data.data;
      return Array.isArray(data) ? data[0] : data;
    } catch (e) {
      console.error("getDependencia>>", e);
      throw e;
    }
  }

  async function setDependenciasList(page = 1) {
    try {
      isLoading.value = true;
      currentPage.value = page;
      const offsetValue = (page - 1) * pageSize.value;
      const url = `${BASE_URL}?limit=${pageSize.value}&offset=${offsetValue}`;
      const r = await rest.get({ url });
      dependenciasList.value = r.data.data;
    } catch (e) {
      console.error("setDependenciasList>>", e);
    } finally {
      isLoading.value = false;
    }
  }

  async function searchDependencias(searchQuery, page = 1) {
    try {
      isLoading.value = true;
      currentPage.value = page;
      const offsetValue = (page - 1) * pageSize.value;
      const url = `${BASE_URL}?limit=${pageSize.value}&offset=${offsetValue}&search=${encodeURIComponent(searchQuery)}`;
      const r = await rest.get({ url });
      dependenciasList.value = r.data.data;
    } catch (e) {
      console.error("searchDependencias>>", e);
    } finally {
      isLoading.value = false;
    }
  }

  async function createDependencia(payload) {
    try {
      const { data } = await rest.post({ url: BASE_URL, formData: payload });
      allDependenciasListLoad.value = false;
      catalogNeedsRefresh.value = true;
      dependenciasListLoad.value = false;
      return data;
    } catch (e) {
      console.error("createDependencia>>", e);
      throw e;
    }
  }

  async function updateDependencia(id, payload) {
    try {
      const { data } = await rest.put({ url: `${BASE_URL}/${id}`, formData: payload });
      allDependenciasListLoad.value = false;
      catalogNeedsRefresh.value = true;
      dependenciasListLoad.value = false;
      return data;
    } catch (e) {
      console.error("updateDependencia>>", e);
      throw e;
    }
  }

  async function deleteDependencia(id) {
    try {
      const { data } = await rest.del({ url: `${BASE_URL}/${id}` });
      allDependenciasListLoad.value = false;
      catalogNeedsRefresh.value = true;
      dependenciasListLoad.value = false;
      await getTotal();
      await setDependenciasList(currentPage.value);
      return data;
    } catch (e) {
      console.error("deleteDependencia>>", e);
      throw e;
    }
  }

  function handlePageChange(event) {
    goToPage(Math.floor(event.first / pageSize.value) + 1);
  }

  function goToPage(page) {
    currentPage.value = page;
    setDependenciasList(page);
  }

  function prevPage() {
    if (currentPage.value > 1) goToPage(currentPage.value - 1);
  }

  function nextPage() {
    if (currentPage.value < totalPages.value) goToPage(currentPage.value + 1);
  }

  return {
    // legacy
    dependencias,
    dependenciasListLoad,
    setDependencias,
    // CRUD
    dependenciasList,
    allDependencias,
    isLoading,
    pageSize,
    currentPage,
    offset,
    totalDependencias,
    totalPages,
    getTotal,
    setAllDependencias,
    getDependencia,
    setDependenciasList,
    searchDependencias,
    createDependencia,
    updateDependencia,
    deleteDependencia,
    handlePageChange,
    goToPage,
    prevPage,
    nextPage,
  };
});
